import React, { useEffect, useMemo, useState } from 'react';

import {
  getMarineSpeciesChecklist,
  getMarineWildlifeContext,
  getUpcomingEvents,
} from '../../api';
import {
  TAXA_OPTIONS,
  eventMatchesTaxa,
  filterSpeciesGroupsByTaxa,
} from '../../lib/checklistTaxa';

const INTENT_OPTIONS = [
  { value: 'observe', label: 'Observe' },
  { value: 'wildlife', label: 'Wildlife' },
  { value: 'tour', label: 'Tour' },
  { value: 'science', label: 'Science' },
];

function defaultDate() {
  return new Date().toISOString().slice(0, 10);
}

function prettyDate(value) {
  if (!value) return 'Timing TBD';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_error) {
    return value;
  }
}

export const RegionalChecklistPanel = ({ lat, lng }) => {
  const [manualLat, setManualLat] = useState(lat ?? 36.95);
  const [manualLng, setManualLng] = useState(lng ?? -122.1);
  const [date, setDate] = useState(defaultDate());
  const [intent, setIntent] = useState('observe');
  const [radiusKm, setRadiusKm] = useState(50);
  const [taxa, setTaxa] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventsPayload, setEventsPayload] = useState(null);
  const [checklistPayload, setChecklistPayload] = useState(null);
  const [wildlifePayload, setWildlifePayload] = useState(null);

  useEffect(() => {
    if (typeof lat === 'number' && Number.isFinite(lat)) setManualLat(lat);
  }, [lat]);

  useEffect(() => {
    if (typeof lng === 'number' && Number.isFinite(lng)) setManualLng(lng);
  }, [lng]);

  useEffect(() => {
    if (!Number.isFinite(Number(manualLat)) || !Number.isFinite(Number(manualLng)) || !date) {
      return;
    }

    let active = true;
    setLoading(true);
    setError('');

    Promise.all([
      getMarineSpeciesChecklist({
        lat: Number(manualLat),
        lon: Number(manualLng),
        date,
        intent,
      }),
      getMarineWildlifeContext({
        lat: Number(manualLat),
        lon: Number(manualLng),
        date,
      }),
      getUpcomingEvents({
        lat: Number(manualLat),
        lon: Number(manualLng),
        radiusKm,
        days: 30,
        interests: ['wildlife'],
        categories: ['wildlife'],
      }),
    ])
      .then(([checklistRes, wildlifeRes, eventsRes]) => {
        if (!active) return;
        setChecklistPayload(checklistRes.data);
        setWildlifePayload(wildlifeRes.data);
        setEventsPayload(eventsRes.data);
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load the regional checklist.');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [date, intent, manualLat, manualLng, radiusKm]);

  const filteredStrataCards = useMemo(() => {
    const cards = Array.isArray(checklistPayload?.strata_cards) ? checklistPayload.strata_cards : [];
    return cards
      .map((card) => ({
        ...card,
        filteredGroups: filterSpeciesGroupsByTaxa(card.likely_species_groups, taxa),
      }))
      .filter((card) => card.filteredGroups.length > 0 || taxa === 'all');
  }, [checklistPayload, taxa]);

  const wildlifeEvents = useMemo(() => {
    const events = Array.isArray(eventsPayload?.events) ? eventsPayload.events : [];
    return events
      .filter((item) => String(item?.category || '').toLowerCase() === 'wildlife')
      .filter((item) => eventMatchesTaxa(item, taxa))
      .slice(0, 5);
  }, [eventsPayload, taxa]);

  const wildlifeNotes = Array.isArray(wildlifePayload?.nearby_pattern_notes)
    ? wildlifePayload.nearby_pattern_notes
    : [];
  const seasonalNotes = Array.isArray(wildlifePayload?.seasonal_context?.notes)
    ? wildlifePayload.seasonal_context.notes
    : [];

  const useBrowserLocation = () => {
    if (!navigator.geolocation) {
      setError('Browser geolocation is unavailable on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setManualLat(Number(position.coords.latitude.toFixed(4)));
        setManualLng(Number(position.coords.longitude.toFixed(4)));
      },
      () => {
        setError('Unable to access browser location right now.');
      }
    );
  };

  return (
    <section className="atlas-sidebar-card atlas-signal-panel">
      <div className="atlas-panel-header">
        <div className="atlas-sidebar-copy atlas-sidebar-copy--flush">
          <h3>Regional species checklist</h3>
          <p>
            This tab blends Tellus&apos;s live wildlife events with the newer habitat-based checklist
            service so the client can still give you a useful regional field list even while the
            old static checklist archive is empty.
          </p>
        </div>
        <button type="button" className="atlas-secondary-link atlas-control-button" onClick={useBrowserLocation}>
          Use my location
        </button>
      </div>

      <div className="atlas-control-grid">
        <label className="atlas-control-label">
          Latitude
          <input
            className="atlas-control-input"
            type="number"
            step="0.01"
            value={manualLat}
            onChange={(event) => setManualLat(event.target.value)}
          />
        </label>
        <label className="atlas-control-label">
          Longitude
          <input
            className="atlas-control-input"
            type="number"
            step="0.01"
            value={manualLng}
            onChange={(event) => setManualLng(event.target.value)}
          />
        </label>
        <label className="atlas-control-label">
          Date
          <input
            className="atlas-control-input"
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <label className="atlas-control-label">
          Intent
          <select
            className="atlas-control-input"
            value={intent}
            onChange={(event) => setIntent(event.target.value)}
          >
            {INTENT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="atlas-control-label">
          Radius km
          <select
            className="atlas-control-input"
            value={radiusKm}
            onChange={(event) => setRadiusKm(Number(event.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
        <label className="atlas-control-label">
          Taxa focus
          <select
            aria-label="Taxa focus"
            className="atlas-control-input"
            value={taxa}
            onChange={(event) => setTaxa(event.target.value)}
          >
            {TAXA_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="atlas-mini-stats">
        <div>
          <strong>{filteredStrataCards.length}</strong>
          <span>habitat cards</span>
        </div>
        <div>
          <strong>{wildlifeEvents.length}</strong>
          <span>wildlife events</span>
        </div>
        <div>
          <strong>{wildlifeNotes.length + seasonalNotes.length}</strong>
          <span>field notes</span>
        </div>
      </div>

      {loading ? <div className="atlas-empty-state">Loading regional checklist…</div> : null}
      {!loading && error ? <div className="atlas-empty-state">{error}</div> : null}

      {!loading && !error ? (
        <div className="atlas-region-checklist-grid">
          <div className="atlas-region-checklist-column">
            <h4>Habitat checklist</h4>
            {filteredStrataCards.length ? (
              filteredStrataCards.map((card) => (
                <article key={card.id} className="atlas-upcoming-card atlas-upcoming-card--dense">
                  <span className="atlas-card-kicker">{card.label}</span>
                  <h4>{card.filteredGroups.length} likely groups</h4>
                  <div className="atlas-chip-row">
                    {card.filteredGroups.map((group) => (
                      <span key={`${card.id}-${group.label}`} className="atlas-chip">
                        {group.label}
                      </span>
                    ))}
                  </div>
                  {Array.isArray(card.cues) && card.cues.length ? (
                    <div className="atlas-detail-stack">
                      <span>
                        <strong>Look for</strong>
                        {card.cues.slice(0, 2).join(', ')}
                      </span>
                    </div>
                  ) : null}
                </article>
              ))
            ) : (
              <div className="atlas-empty-state">
                No checklist groups matched that taxa focus in the current region.
              </div>
            )}
          </div>

          <div className="atlas-region-checklist-column">
            <h4>Wildlife moments and notes</h4>
            {wildlifeEvents.length ? (
              wildlifeEvents.map((event) => (
                <article key={event.id || event.title} className="atlas-upcoming-card atlas-upcoming-card--dense">
                  <span className="atlas-card-kicker">{event.category_label || event.category}</span>
                  <h4>{event.title}</h4>
                  <p>{event.summary}</p>
                  <div className="atlas-detail-stack">
                    <span>
                      <strong>Starts</strong>
                      {prettyDate(event.starts_at)}
                    </span>
                    <span>
                      <strong>Distance</strong>
                      {event.distance_km != null ? `${event.distance_km.toFixed(1)} km` : 'Broad-region signal'}
                    </span>
                  </div>
                </article>
              ))
            ) : (
              <div className="atlas-empty-state">
                No wildlife event cards matched that taxa focus right now.
              </div>
            )}

            {[...wildlifeNotes, ...seasonalNotes].length ? (
              <article className="atlas-upcoming-card atlas-upcoming-card--dense">
                <span className="atlas-card-kicker">Field notes</span>
                <h4>Pattern guidance</h4>
                <ul className="atlas-bullet-list atlas-bullet-list--compact">
                  {[...wildlifeNotes, ...seasonalNotes].slice(0, 5).map((note) => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </article>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
};
